/** "Which ground does this client actually own?" — answered by the county, not by
 *  reading CC&Rs (field feedback 2026-07-25).
 *
 *  Maricopa records HOA common area as its own parcels: PUC 0261, owned by the
 *  association by name. So the app can list the association's tracts with real
 *  polygons and real square footage, and the user just picks whose ground to bid.
 *  No OpenStreetMap, no document hunting, no drawing.
 */
import { useState } from 'preact/hooks';
import { createRecord, createRecords } from '../../core/db';
import { nowIso } from '../../core/ids';
import { ServiceError } from '../../core/net';
import { findCommonAreaTracts, groupByOwner, type Tract } from '../../providers/tracts';
import type { Job, PriceItem } from '../../core/types';
import { toast } from '../state';

interface Props {
  job: Job;
  prices: PriceItem[];
  user: string;
  viewBox: [number, number, number, number] | null;
  onChanged: () => Promise<void>;
}

export function TractFinder({ job, prices, user, viewBox, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [groups, setGroups] = useState<{ owner: string; tracts: Tract[]; totalSqFt: number }[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [priceFor, setPriceFor] = useState<Record<string, string>>({});

  const platName = job.parcel?.provider === 'maricopa-subdivision'
    ? (job.parcel.label ?? '').split(' · ')[0]
    : undefined;

  async function look() {
    setBusy(true); setNote(null); setGroups(null);
    try {
      const tracts = await findCommonAreaTracts(platName ? { subname: platName } : { bbox: viewBox ?? undefined });
      if (!tracts.length) {
        setNote(platName
          ? 'The county lists no association-owned tracts inside this plat. The commons may be recorded under a neighboring plat — try searching the community name without the number, or outline the areas by hand.'
          : 'No association-owned parcels here. Zoom the map to the community and try again, or outline the areas by hand.');
        return;
      }
      setGroups(groupByOwner(tracts));
    } catch (e) {
      setNote(e instanceof ServiceError
        ? `${e.message} You can still outline the areas by hand — that path never depends on the county.`
        : 'That county lookup failed. You can still outline the areas by hand.');
    } finally { setBusy(false); }
  }

  async function addGroup(owner: string, tracts: Tract[]) {
    setBusy(true);
    try {
      const itemId = priceFor[owner] ?? '';
      const price = prices.find((p) => p.itemId === itemId);
      let added = 0;
      for (const t of tracts) {
        for (const ring of t.rings) {
          const shape = await createRecord('shapes', {
            jobId: job.id, kind: 'polygon', coords: ring,
            measure: { value: Math.round(t.measuredSqFt), unit: 'sqft', method: 'karney', computedAt: nowIso() },
          }, { snapshot: false });
          if (price) {
            await createRecord('takeoffLines', {
              jobId: job.id, priceItemId: price.itemId,
              qty: Math.round(t.measuredSqFt),
              label: `${price.name.split('(')[0]!.trim()} — APN ${t.apn}`,
              shapeId: shape.id,
              provenance: { source: 'scan', setBy: user, setAt: nowIso(), detail: `county-tract ${t.apn}` },
              verification: { verified: false },
            }, { snapshot: false });
          }
          added++;
          break; // outer ring only; holes are rare on these tracts and would double-count
        }
      }
      await onChanged();
      toast(price
        ? `${added} tract(s) added and priced as ${price.name.split('(')[0]!.trim()} — unverified until you walk them.`
        : `${added} tract outline(s) added to the map. Total ${tracts.reduce((s, t) => s + t.measuredSqFt, 0).toLocaleString('en-US')} sq ft.`);
    } finally { setBusy(false); }
  }

  const areaItems = prices.filter((p) => !p.retired && p.kind === 'area');

  return (
    <div>
      <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
        <button class="btn btn-small btn-primary" disabled={busy} onClick={() => void look()}
          title="County records list the parcels an HOA owns: the strips, basins, entries and islands">
          {busy ? 'Asking the county…' : 'Show the areas the HOA owns'}
        </button>
        <span class="small muted">
          {platName ? `Inside ${platName}` : 'In what the map is showing'}
        </span>
      </div>

      {note && (
        <div class="banner banner-info" style="margin:8px 0 0;" role="status">
          <div>{note}</div>
          <button class="btn btn-small btn-ghost" onClick={() => setNote(null)}>OK</button>
        </div>
      )}

      {groups && (
        <div class="card" style="margin-top:8px;padding:12px;">
          <p class="small muted" style="margin-bottom:8px;">
            County records, by owner. Pick the association you are bidding — the others are
            the neighbors’ ground.
          </p>
          {groups.map((g) => (
            <div key={g.owner} style="border-top:1px solid var(--ink-20);padding:10px 0;">
              <div style="font-weight:600;">{g.owner}</div>
              <div class="small muted num">
                {g.tracts.length} tract{g.tracts.length === 1 ? '' : 's'} · {g.totalSqFt.toLocaleString('en-US')} sq ft measured
                {' '}({(g.totalSqFt / 43560).toFixed(2)} acres)
              </div>
              <ul class="small muted" style="list-style:none;margin:6px 0 0;">
                {g.tracts.slice(0, 6).map((t) => (
                  <li key={t.apn} class="num" style="padding:1px 0;">
                    APN {t.apn} — {t.measuredSqFt.toLocaleString('en-US')} sq ft
                    {t.countySqFt ? <span> (county says {t.countySqFt.toLocaleString('en-US')})</span> : null}
                  </li>
                ))}
                {g.tracts.length > 6 && <li>+{g.tracts.length - 6} more</li>}
              </ul>
              <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-top:8px;">
                <select value={priceFor[g.owner] ?? ''} aria-label={`Price ${g.owner} tracts as`}
                  style="min-height:34px;max-width:240px;"
                  onChange={(e) => setPriceFor({ ...priceFor, [g.owner]: (e.target as HTMLSelectElement).value })}>
                  <option value="">Just outline on the map</option>
                  {areaItems.map((p) => <option key={p.itemId} value={p.itemId}>Price as {p.name}</option>)}
                </select>
                <button class="btn btn-small btn-ghost" disabled={busy} onClick={() => void addGroup(g.owner, g.tracts)}>
                  Add these tracts
                </button>
              </div>
            </div>
          ))}
          <p class="small muted" style="margin-top:10px;">
            Square footage is measured from the county’s own polygons. Whether every tract is
            in the contract is still a question for the client — the walk-through sheet is where
            that gets confirmed.
          </p>
        </div>
      )}
    </div>
  );
}

export { };
